import { useEffect, useState } from 'react';
import Link from 'next/link';
import { RichText } from 'prismic-reactjs';
import { AddToShoppingCartSource } from '@monofront/types';
import { default as CarouselProductsComponent } from '@components/CarouselProducts';
import CustomPrismicLink from '@components/CustomPrismicLink';
import CustomPrismicHeading from '@components/CustomPrismicHeading';
import withSlice from '@screens/SlicesContainer/withSlice';
import { mapMultipleProductsCarousel } from './mapMultipleProductsCarousels';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { IMultipleProductsCarousels } from './types';
import { preloadImg } from '@monofront/business/utils';

const MultipleProductsCarousel: React.FC<IMultipleProductsCarousels> = ({
  items,
  primary,
}) => {
  const [displayedCarouselIndex, setDisplayedCarouselIndex] = useState(0);

  useEffect(() => {
    // Preload images of hidden carousels. Use setTimeout to make it lazy.
    const timeout = setTimeout(() => {
      items?.forEach((carousel, index) => {
        if (index === displayedCarouselIndex) {
          return;
        }

        carousel.algoliaProducts?.forEach((product) => {
          if (product?.images?.length) {
            const image = product.images[0];

            preloadImg(`${image}?width=312&quality=60`);
          }
        });
      });
    }, 0);

    return () => clearTimeout(timeout);
  }, [items]);

  if (!primary || !items || !items.length) return null;

  const carousel = items[displayedCarouselIndex];
  const index = displayedCarouselIndex;

  return (
    <>
      <section className="multiple-carousel-products">
        <div className="title-block">
          <RichText
            render={primary.title}
            htmlSerializer={CustomPrismicHeading({
              className: 'catchy-title-main',
            })}
          />
          {primary.titleIcon?.url && (
            <span className="title-icon">
              <ResponsiveImage
                width={primary.titleIcon.dimensions.width}
                height={primary.titleIcon.dimensions.height}
                src={primary.titleIcon.url}
                alt={primary.titleIcon.alt}
              />
            </span>
          )}
        </div>

        <div className="carousels-menu">
          <ul>
            {items.map(({ menuItem, collection }, index) => {
              return (
                <li key={index}>
                  <a
                    href={`#carousel-${index}-${collection?.handle}`}
                    className={displayedCarouselIndex === index ? 'active' : ''}
                    onClick={(e) => {
                      e.preventDefault();
                      setDisplayedCarouselIndex(index);
                    }}
                  >
                    {menuItem}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>

        {carousel.algoliaProducts?.length && (
          <div
            id={`#carousel-${index}-${carousel.collection?.handle}`}
            className={`carousel-wrapper ${
              displayedCarouselIndex === index ? 'active' : ''
            }`}
          >
            <CarouselProductsComponent
              headerTitle={
                <div className="header">
                  <div className="title">
                    <RichText
                      render={carousel.title}
                      htmlSerializer={CustomPrismicHeading({
                        className: 'catchy-title-s',
                      })}
                    />
                    {carousel.collectionLink && (
                      <div className="link-container">
                        <Link
                          href={`/collections/${
                            carousel.collectionLink?.handle
                          }${
                            carousel.trackingId
                              ? `?trackingId=${carousel.trackingId}`
                              : ''
                          }`}
                          legacyBehavior
                        >
                          <a className="tw-lf-link-md tw-whitespace-nowrap">
                            <span>{carousel.textLink}</span>
                          </a>
                        </Link>
                      </div>
                    )}
                  </div>
                  <RichText
                    render={carousel.text}
                    serializeHyperlink={CustomPrismicLink}
                  />
                </div>
              }
              products={carousel.algoliaProducts}
              trackingId={carousel.trackingId || undefined}
              containerContext={{
                source: AddToShoppingCartSource.CAROUSEL,
                widgetName: carousel.trackingId || undefined,
              }}
            />
          </div>
        )}
      </section>
      <style jsx>{`
        .title-block {
          display: flex;
          align-items: baseline;
          margin-bottom: var(--space-5);
        }

        .carousels-menu {
          position: relative;
        }

        .carousels-menu:after {
          content: '';
          position: absolute;
          right: 0;
          top: 0;
          bottom: 0;
          width: var(--padding-layout-horizontal-base);
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0) 0%,
            #ffffff 100%
          );
        }

        .carousels-menu ul {
          display: block;
          margin-top: var(--negative-space-3);
          margin-bottom: var(--space-5);
          padding-right: var(--padding-layout-horizontal-base);
          width: 100%;
          white-space: nowrap;
          overflow-x: auto;
        }

        .carousels-menu li {
          display: inline-block;
          padding: var(--space-2) 0;
        }

        .carousels-menu a {
          margin-right: var(--space-3);
          padding: 2px 8px;
          border: 1px solid var(--clr-primary-1);
          border-radius: var(--rd-5);
          font-size: var(--font-size-2);
          font-weight: var(--font-weight-bold);
          white-space: nowrap;
        }

        .carousels-menu a:hover {
          background-color: var(--clr-primary-2);
        }

        .carousels-menu .active {
          background-color: var(--clr-primary-1);
        }

        .carousel-wrapper {
          display: none;
        }

        .carousel-wrapper.active {
          display: block;
        }

        .header {
          display: flex;
          flex-direction: column;
          margin-bottom: var(--space-4);
        }

        .title {
          display: flex;
          justify-content: space-between;
          width: 100%;
        }

        .link-container {
          display: flex;
          flex-direction: column;
          justify-content: center;
          margin-left: var(--space-2);
        }

        @media (max-width: 800px) {
          .title-block {
            margin-top: var(--negative-space-7);
            margin-bottom: var(--space-6);
            justify-content: center;
            padding-left: var(--space-8);
          }

          .title-block .title-icon {
            flex-shrink: 0;
          }

          .carousels-menu {
            margin: var(--negative-padding-layout-base);
            margin-top: var(--negative-space-3);
            margin-bottom: var(--space-5);
            width: auto;
          }

          .carousels-menu:before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: var(--padding-layout-horizontal-base);
            background: linear-gradient(
              -90deg,
              rgba(255, 255, 255, 0) 0%,
              #ffffff 100%
            );
          }

          .carousels-menu ul {
            padding: var(--padding-layout-base);
          }

          .multiple-carousel-products :global(.swiper) {
            margin: var(--negative-padding-layout-base);
            padding: var(--padding-layout-base);
            width: 100vw;
          }
        }
      `}</style>
    </>
  );
};

export default withSlice(MultipleProductsCarousel, mapMultipleProductsCarousel);
